import { useEffect, useState } from 'react'
import { HomeworkService, type HomeworkAttachment } from '../services/homework'

interface HomeworkAttachmentListProps {
  studentId: string
  homeworkId: string
  /** Optional heading shown above the list (hidden when there are no attachments) */
  label?: string
}

// Read-only list of a student's submission attachments with signed-URL downloads.
// Used both in the student feedback view and the teacher review panel. Renders
// nothing when the student attached no files.
const HomeworkAttachmentList = ({ studentId, homeworkId, label = '添付ファイル' }: HomeworkAttachmentListProps) => {
  const [attachments, setAttachments] = useState<HomeworkAttachment[]>([])
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    HomeworkService.getSubmissionAttachments(studentId, homeworkId)
      .then(a => { if (!cancelled) setAttachments(a) })
      .catch(e => console.error(e))
    return () => { cancelled = true }
  }, [studentId, homeworkId])

  const handleDownload = async (att: HomeworkAttachment) => {
    setError('')
    setDownloadingId(att.id)
    try {
      const url = await HomeworkService.getAttachmentDownloadUrl(att.file_path, att.file_name)
      // The signed URL is served with Content-Disposition: attachment, so navigating
      // via an anchor downloads without opening a tab (window.open after an await
      // loses the user gesture and gets silently swallowed by popup blockers).
      const link = document.createElement('a')
      link.href = url
      link.download = att.file_name
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (e) {
      console.error(e)
      setError('ダウンロードに失敗しました。')
    } finally {
      setDownloadingId(null)
    }
  }

  if (attachments.length === 0) return null

  return (
    <div>
      <p className="text-xs font-semibold text-gray-600 mb-2">{label}</p>
      <ul className="flex flex-col gap-1.5">
        {attachments.map(att => (
          <li key={att.id}>
            <button
              type="button"
              onClick={() => handleDownload(att)}
              disabled={downloadingId === att.id}
              className="w-full flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-left hover:bg-gray-100 disabled:opacity-50"
            >
              <span className="text-base">{HomeworkService.getFileIcon(att.mime_type)}</span>
              <span className="flex-1 min-w-0 truncate text-sm text-[#da4574] underline">{att.file_name}</span>
              <span className="text-xs text-gray-400 whitespace-nowrap">{HomeworkService.formatFileSize(att.file_size)}</span>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
    </div>
  )
}

export default HomeworkAttachmentList
